import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import { useCallback, useEffect, useState } from "react";
import { IOrganizations } from "../../../../../setup/types/Organisation";
import Spinner from "../../../../../components/common/Spinner";
import BusinessTable, { RowWithOptionalClick } from "../../../table";
import React from "react";
import AddUserModal, { Inputs, PERMISSION_OPTIONS } from "./addAdminModal";
import PrimaryButton from "../../../../../components/common/PrimaryButton";
import {
  createClientAdmin,
  editClientAdminPermissions,
  getClientAdmins,
  resendClientAdminInvite,
} from "../../../../../setup/api/services/user";
import SuccessModal from "./SuccessModal";
import { AuthConfig } from "../../../../../setup/context/config/authConfig";
import Pagination from "../../../pagination";
import { toast, ToastContainer } from "react-toastify";
import {
  FormError,
  parseApiErrors,
} from "../../../../../setup/util/apiErrors";

/** Own toast container so the messages show wherever this tab is mounted, without duplicates. */
const USERS_TOASTS = "users-information";

interface UsersInformationProps {
  organization: IOrganizations | undefined;
}

// Define tier information structure
interface UserInfo extends RowWithOptionalClick {
  name: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  state: string | undefined;
  permissions: string[];
  orgOwner: boolean;
  id: number;
}

/** "All permissions" stands for the rest; otherwise each permission the dialog offers, in its order. */
const permissionLabels = (permissions: string[] = []) => {
  if (permissions.includes("CLIENT_ADMIN")) {
    return PERMISSION_OPTIONS.filter((option) => option.id === "CLIENT_ADMIN");
  }
  return PERMISSION_OPTIONS.filter((option) => permissions.includes(option.id));
};

const UsersInformation = ({ organization }: UsersInformationProps) => {
  const { t } = useTranslation();
  const authorities = AuthConfig.getUserInfo()?.authorities;
  const [mode, setMode] = useState<"add" | "edit">("add");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [invite, setInvite] = useState({
    id: 0,
    email: "",
    sent: false,
    existingAccount: false,
  });
  const [resending, setResending] = useState(false);
  const [serverErrors, setServerErrors] = useState<FormError[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const pageSize = 10;

  // Define table columns
  const columns = [
    { type: "name", value: t("lbl_username") },
    { type: "phoneNumber", value: t("mobile_number") },
    { type: "email", value: t("email") },
    {
      type: "permissions",
      value: t("lbl_permissions"),
    },
    { type: "state", value: t("lbl_status") },
    { type: "actions", value: t("lbl_edit") },
  ];

  const fetchOrganizationAdmins = useCallback(async () => {
    if (!organization) return;
    setIsLoading(true);
    try {
      const response = await getClientAdmins(page - 1, pageSize);

      setUsers(
        response?.data?.content.map((clientAdmin) => ({
          id: +clientAdmin?.userIdentification,
          name: clientAdmin?.profile?.fullName,
          firstName: clientAdmin?.profile?.firstName || "",
          lastName: clientAdmin?.profile?.lastName || "",
          phoneNumber: clientAdmin?.phoneNumber,
          email: clientAdmin?.email || "",
          // Waiting for the person to choose a password (the invitation was not accepted yet).
          state: clientAdmin?.invitationPending ? "Inactive" : "Active",
          permissions: clientAdmin?.authorities,
          orgOwner: clientAdmin?.orgOwner,
        }))
      );

      setPagesCount(response.data.totalPages);
      setTotalCount(response.data.totalElements);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [organization, page]);

  useEffect(() => {
    fetchOrganizationAdmins();
  }, [organization, t, fetchOrganizationAdmins, page]);

  const handlePaginationChange = (value: number) => {
    setPage(value);
  };

  const handleEdit = (index) => {
    setMode("edit");
    setSelectedIndex(index);
    setModalOpen(true);
  };

  const resendInvite = async (id: number) => {
    setResending(true);
    try {
      await resendClientAdminInvite(id);
      setInvite((current) => ({ ...current, sent: true }));
      toast.success(t("lbl_invite_resent"), { containerId: USERS_TOASTS });
    } catch (error) {
      toast.error(
        parseApiErrors(error, t("lbl_invite_resend_failed"))[0].message,
        { containerId: USERS_TOASTS }
      );
    } finally {
      setResending(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner />
      </div>
    );
  }

  // Render cell based on column type and data
  const renderCell = (columnType: string, row: UserInfo) => {
    switch (columnType) {
      case "name":
        return <span className={styles.nameValue}>{row.name}</span>;
      case "phoneNumber":
        return (
          <span className={styles.nameValue} style={{ direction: "ltr" }}>
            {row?.phoneNumber}
          </span>
        );
      case "email":
        return <span className={styles.nameValue}>{row?.email}</span>;
      case "permissions":
        return (
          <div className={styles.chips}>
            {row?.orgOwner && (
              <span className={`${styles.chip} ${styles.chipOwner}`}>
                {t("lbl_org_owner")}
              </span>
            )}
            {permissionLabels(row?.permissions).map((permission) => (
              <span
                key={permission.id}
                className={`${styles.chip} ${
                  permission.id === "CLIENT_ADMIN" ? styles.chipAll : ""
                } ${permission.id === "CLIENT_DEVELOPER" ? styles.chipDev : ""}`}>
                {permission.label}
              </span>
            ))}
          </div>
        );
      case "state":
        return (
          <span className={styles.statusContainer}>
            <span
              className={`${styles.statusChip} ${
                row.state === "Active" ? styles.statusActive : styles.statusInactive
              }`}>
              {row.state === "Active"
                ? t("lbl_user_active")
                : t("lbl_user_inactive")}
            </span>
            {row.state === "Inactive" && !row?.orgOwner && (
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => resendInvite(row.id)}>
                {t("lbl_resend_invite")}
              </button>
            )}
          </span>
        );
      case "actions":
        return (
          <div className={styles.actionsContainer}>
            {!row?.orgOwner && (
              <button
                type="button"
                className={styles.linkButton}
                onClick={handleEdit?.bind(
                  null,
                  users?.findIndex((user) => user?.id === row?.id)
                )}>
                <img
                  src="/assets/images/svg/edit.svg"
                  alt="edit"
                  style={{ width: "17px", height: "17px" }}
                />
              </button>
            )}

            {/* {!row?.orgOwner && (
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => {
                  setSelectedIndex(
                    users?.findIndex((user) => user?.id === row?.id)
                  );
                }}>
                <img
                  src="/assets/images/svg/deleteAlt.svg"
                  alt="delete"
                  style={{ width: "17px", height: "17px" }}
                />
              </button>
            )} */}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <ToastContainer containerId={USERS_TOASTS} />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          width: "100%",
        }}>
        <div className={styles.headerContainer}>
          <h3 className={styles.title}>إدارة المستخدمين</h3>
          <p className={styles.subtitle}>
            يمكنك إضافة مستخدمين جدد أو تعديل صلاحيات المستخدمين الحاليين
          </p>
        </div>
        <div className={styles.addButton}>
          {(authorities?.includes("CLIENT_ADMIN_CREATE_USER") ||
            authorities?.includes("CLIENT_ADMIN") ||
            authorities?.includes("ORGANIZATION_OWNER")) && (
            <PrimaryButton
              onClick={() => {
                setMode("add");
                setModalOpen(true);
              }}>
              {`+ ${t("lbl_add_user")}`}
            </PrimaryButton>
          )}
        </div>
      </div>
      <div className={styles.tableContainer}>
        <UsersBusinessTable
          columns={columns}
          rows={users}
          renderCell={(columnType, row) =>
            renderCell(columnType, row as UserInfo)
          }
          pagesCount={pagesCount}
          page={page}
          pageSize={pageSize}
          handlePaginationChange={handlePaginationChange}
          totalCount={totalCount}
        />
      </div>

      <SuccessModal
        modalOpen={successModalOpen}
        handleClose={() => {
          setSuccessModalOpen(false);
          fetchOrganizationAdmins();
        }}
        email={invite.email}
        inviteSent={invite.sent}
        existingAccount={invite.existingAccount}
        resending={resending}
        onResend={() => resendInvite(invite.id)}
      />

      <AddUserModal
        loading={loading}
        modalOpen={modalOpen}
        serverErrors={serverErrors}
        handleClose={() => {
          setModalOpen(false);
          setServerErrors([]);
          if (mode === "edit") fetchOrganizationAdmins();
        }}
        handleSave={async (data: Inputs) => {
          setLoading(true);
          setServerErrors([]);
          try {
            if (mode === "add") {
              const result = await createClientAdmin({
                email: data.email.trim(),
                authorities: [...data.permissions],
                clientUserId: data.phoneNumber,
                phoneNumber: data.phoneNumber,
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
              });
              setInvite({
                id: Number(result?.data?.userId ?? result?.data?.userIdentification ?? 0),
                email: result?.data?.email ?? data.email,
                sent: !!result?.data?.inviteEmailSent,
                existingAccount: !!result?.data?.existingAccount,
              });
              setModalOpen(false);
              setSuccessModalOpen(true);
            } else {
              await editClientAdminPermissions(users?.[selectedIndex]?.id, {
                roles: [...data.permissions],
              });
              setModalOpen(false);
              fetchOrganizationAdmins();
            }
          } catch (error) {
            console.error(error);
            setServerErrors(parseApiErrors(error, t("lbl_add_user_failed")));
          } finally {
            setLoading(false);
          }
        }}
        {...(mode === "edit"
          ? {
              initialData: users?.[selectedIndex],
            }
          : {})}
        mode={mode}
      />
    </div>
  );
};

// Custom wrapper for BusinessTable specifically for UsersInformation
const UsersBusinessTable = ({
  columns,
  rows,
  renderCell,
  pagesCount,
  page,
  pageSize,
  handlePaginationChange,
  totalCount,
}: {
  columns: { type: string; value: string }[];
  rows: UserInfo[];
  renderCell: (
    column: string,
    row: UserInfo,
    rowIndex: number
  ) => React.ReactNode;
  pagesCount: number;
  page: number;
  pageSize: number;
  handlePaginationChange: (value: number) => void;
  totalCount: number;
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.usersTableWrapper}>
      <BusinessTable
        columns={columns}
        rows={rows}
        renderCell={(column, row, rowIndex) =>
          renderCell(column, row as UserInfo, rowIndex)
        }
      />
      <Pagination
        countName={t("lbl_user")}
        pagesCount={pagesCount}
        length={totalCount}
        page={page}
        pageSize={pageSize}
        handlePaginationChange={handlePaginationChange}
      />
    </div>
  );
};

export default UsersInformation;
